import { api } from './api-client';
import type { ApprovalItem, ApprovalPriority } from '@/hooks/useApprovals';

/**
 * Backend /approval/tasks shape — kept narrow to what the UI needs, tolerate extras.
 * Matches desktop's ApprovalTask (see ERP_OA/apps/desktop/.../useApprovalData.ts).
 */
interface BackendTask {
  id: string;
  taskNumber?: string;
  bizType?: string;
  bizNumber?: string;
  title?: string;
  summary?: string;
  status?: string;
  priority?: string;
  amount?: number | string | null;
  applicantName?: string;
  departmentName?: string;
  createdAt?: string;
}

interface BizMeta {
  typeKey: string;
  icon: string;
  priority: ApprovalPriority;
}

const BIZ_LOOKUP: Record<string, BizMeta> = {
  LEAVE: { typeKey: 'approvals.type_leave', icon: 'event_busy', priority: 'normal' },
  HR_LEAVE: { typeKey: 'approvals.type_leave', icon: 'event_busy', priority: 'normal' },
  EXPENSE: { typeKey: 'approvals.type_expense', icon: 'receipt_long', priority: 'expense' },
  HR_EXPENSE: { typeKey: 'approvals.type_expense', icon: 'receipt_long', priority: 'expense' },
  OVERTIME: { typeKey: 'approvals.type_overtime', icon: 'more_time', priority: 'normal' },
  HR_OVERTIME: { typeKey: 'approvals.type_overtime', icon: 'more_time', priority: 'normal' },
  TRAVEL: { typeKey: 'approvals.type_travel', icon: 'flight_takeoff', priority: 'normal' },
  HR_TRAVEL: { typeKey: 'approvals.type_travel', icon: 'flight_takeoff', priority: 'normal' },
  JOURNAL_ENTRY: { typeKey: 'approvals.type_journal', icon: 'account_balance', priority: 'expense' },
  AP_MATCH_EXCEPTION: { typeKey: 'approvals.type_ap_match', icon: 'payments', priority: 'expense' },
  AR_CREDIT_RELEASE: { typeKey: 'approvals.type_ar_credit', icon: 'credit_card', priority: 'normal' },
};

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function formatAmount(amount: BackendTask['amount']): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return n.toLocaleString();
}

function adapt(task: BackendTask): ApprovalItem {
  const meta = BIZ_LOOKUP[task.bizType ?? ''] ?? {
    typeKey: 'approvals.type_unknown',
    icon: 'task_alt',
    priority: 'normal' as ApprovalPriority,
  };
  const isUrgent = task.priority === 'URGENT' || task.priority === 'high';
  const priority: ApprovalPriority = isUrgent ? 'urgent' : meta.priority;
  const iconColorVar =
    priority === 'expense'
      ? 'var(--status-pending)'
      : priority === 'urgent'
        ? 'var(--status-alert)'
        : 'var(--primary)';
  const iconBgVar =
    priority === 'expense'
      ? 'var(--status-pending-bg)'
      : priority === 'urgent'
        ? 'var(--status-alert-bg)'
        : 'var(--primary-subtle)';

  const resolution: 'approved' | 'rejected' | undefined =
    task.status === 'APPROVED'
      ? 'approved'
      : task.status === 'REJECTED' || task.status === 'CANCELLED'
        ? 'rejected'
        : undefined;

  const base: ApprovalItem = {
    id: task.id,
    bizType: task.bizType ?? 'UNKNOWN',
    typeKey: meta.typeKey,
    title: task.title ?? task.taskNumber ?? '(no title)',
    createdAt: task.createdAt ?? new Date().toISOString(),
    icon: meta.icon,
    iconColorVar,
    iconBgVar,
    priority,
    requesterName: task.applicantName ?? 'Unknown',
    requesterDept: task.departmentName ?? '',
    requesterInitials: initials(task.applicantName),
    amount: formatAmount(task.amount),
  };
  return resolution ? { ...base, resolution } : base;
}

export async function fetchPendingTasks(): Promise<ApprovalItem[]> {
  const { data } = await api.get<BackendTask[]>('/approval/tasks');
  return data.map(adapt);
}

export async function fetchMySubmissions(): Promise<ApprovalItem[]> {
  const { data } = await api.get<BackendTask[]>('/approval/tasks/my-submissions');
  return data.map(adapt);
}

/**
 * History = my submissions that have left IN_PROGRESS. Desktop does something
 * similar (filters by status client-side). Keeps the UI coherent without
 * requiring the ADMIN-only /tasks/search endpoint.
 */
export async function fetchHistory(): Promise<ApprovalItem[]> {
  const { data } = await api.get<BackendTask[]>('/approval/tasks/my-submissions');
  return data
    .filter((t) => t.status && t.status !== 'IN_PROGRESS' && t.status !== 'PENDING')
    .map(adapt);
}

export interface ApprovalDetail {
  id: string;
  taskNumber: string;
  bizType: string;
  bizNumber?: string;
  title: string;
  summary?: string;
  amount?: number | string | null;
  status: string;
  currentNode: number;
  applicantName: string;
  departmentName?: string;
  createdAt: string;
  flowSnapshot: Array<{ name: string; order: number; approverType?: string; approverValue?: string }>;
  records: Array<{
    id: string;
    nodeOrder: number;
    nodeName: string;
    approverName: string;
    action: string;
    comment: string;
    createdAt: string;
  }>;
}

export async function fetchTaskDetail(id: string): Promise<ApprovalDetail> {
  const { data } = await api.get<ApprovalDetail>(`/approval/tasks/${id}`);
  return data;
}

export async function approveTask(id: string, comment = ''): Promise<void> {
  await api.post(`/approval/tasks/${id}/approve`, { comment });
}

export async function rejectTask(id: string, comment: string): Promise<void> {
  await api.post(`/approval/tasks/${id}/reject`, { comment });
}
